/*
  The pieces the game, set and card headers share, so the three inner pages read as one
  family (Robert, 2026-10-20: the set and card pages "as modern as" the game page).

    - HEADER_CARD / HEADER_GLOW: the rounded card with the gold glow in its top-right
      corner, first drawn for GameHeader.
    - HeaderFigure: one labelled figure tile; `accent` makes it the big gold one, `note`
      adds a small muted line under the value.
    - HeaderPill: the bordered pill the languages and card facts sit in.
    - HeaderRule: the short gold rule under a header's h1.
*/

import type { ReactNode } from 'react';

import { CONTROL_LABEL } from 'components/styles';

export const HEADER_CARD =
  'rounded-2xl border border-pcg-border bg-pcg-card p-5 shadow-lg sm:p-6';

export const HEADER_GLOW = {
  backgroundImage: 'radial-gradient(90% 80% at 100% 0%, rgb(var(--pcg-gold) / 0.12), transparent 60%)',
};

/* The h1 of an inner page's header. */
export const HEADER_TITLE =
  'font-display text-3xl font-bold uppercase leading-tight text-pcg-text sm:text-4xl';

/* The gold eyebrow line above it. */
export const HEADER_EYEBROW = 'text-xs font-bold uppercase tracking-[0.16em] text-pcg-goldText';

export const HeaderRule = () => (
  <span aria-hidden="true" className="mt-3 block h-[3px] w-14 rounded-full bg-pcg-gold" />
);

interface HeaderFigureProps {
  label: string;
  value: ReactNode;
  accent?: boolean;
  note?: ReactNode;
}

export const HeaderFigure = ({ label, value, accent = false, note }: HeaderFigureProps) => (
  <div className="min-w-0 rounded-xl border border-pcg-borderSoft bg-pcg-raised/40 px-4 py-3">
    {/* Labels wrap rather than truncate: on a phone the tiles are half the screen wide. */}
    <dt className={`${CONTROL_LABEL} leading-snug`}>{label}</dt>
    <dd
      className={`mt-1 font-bold tabular-nums ${
        accent ? 'text-2xl leading-tight text-pcg-goldDisplay sm:text-3xl' : 'text-xl text-pcg-text'
      }`}
    >
      {value}
    </dd>
    {note ? <dd className="mt-0.5 text-xs text-pcg-textMuted">{note}</dd> : null}
  </div>
);

export const HeaderPill = ({ children }: { children: ReactNode }) => (
  <span className="rounded-full border border-pcg-border px-2.5 py-0.5 text-xs font-medium text-pcg-text">
    {children}
  </span>
);
